import { BasedNode, UiSchemaProp } from '../../types/schema.js'
import { getPropType, SchemaAnyProp, SchemaTimestamp } from '@based/sdk/schema'
import { Text } from '../Text/index.js'
import { Badge } from '../Badge/index.js'
import { prettyDate } from '@based/pretty-date'
import { prettyNumber } from '@based/pretty-number'
import { Stack } from '../Stack/index.js'
import { parseValueFromSchema } from '../../utils/parseValueFromSchema.js'
import { Media } from '../Media/index.js'
import { ReactNode } from 'react'

type SchemaComponentMappingProps = {
  schemaProp: UiSchemaProp
  cellData: any
  rowData: BasedNode
  allRowsData: BasedNode[]
  colKey?: string
  maxWidth?: number
}

const CellWrapper: React.FC<{
  children: ReactNode
  justify?: 'start' | 'center' | 'end'
}> = ({ children, justify }) => (
  <Stack
    align="center"
    justify={justify}
    style={{ height: '100%', width: '100%' }}
  >
    {children}
  </Stack>
)

export const SchemaComponentMapping: React.FC<SchemaComponentMappingProps> = ({
  schemaProp,
  cellData,
  rowData,
  allRowsData,
  colKey,
  maxWidth,
}) => {
  if (schemaProp?.render) {
    const result = schemaProp.render(cellData, rowData, allRowsData)

    return result
  }

  if (cellData === null || !schemaProp) {
    return
  }

  if (colKey?.toLocaleLowerCase() === 'src') {
    return (
      <CellWrapper>
        <Media src={cellData} size="small" />
      </CellWrapper>
    )
  }

  const propType = getPropType(schemaProp as SchemaAnyProp)
  const justify =
    schemaProp.align === 'right'
      ? 'end'
      : schemaProp.align === 'center'
        ? 'center'
        : 'start'

  switch (propType) {
    case 'string':
    case 'text':
    case 'alias': {
      return (
        <CellWrapper justify={justify}>
          <Text singleLine variant="display-regular" maxWidth={maxWidth}>
            {parseValueFromSchema(cellData)}
          </Text>
        </CellWrapper>
      )
    }

    case 'number':
    case 'int8':
    case 'int16':
    case 'int32':
    case 'uint8':
    case 'uint16':
    case 'uint32': {
      return (
        <CellWrapper justify={justify}>
          <Text singleLine variant="display-mono" maxWidth={maxWidth}>
            {cellData && colKey.toLocaleLowerCase() === 'size'
              ? prettyNumber(parseValueFromSchema(cellData), 'number-bytes')
              : cellData
                ? prettyNumber(parseValueFromSchema(cellData), 'number-human')
                : ''}
          </Text>
        </CellWrapper>
      )
    }

    case 'timestamp': {
      let dateString = ''
      if (cellData) {
        dateString = prettyDate(
          parseValueFromSchema(cellData),
          (schemaProp as SchemaTimestamp)?.display ?? 'date-time-human',
        )

        dateString = dateString[0].toUpperCase() + dateString.slice(1)
      }
      return (
        <CellWrapper justify={justify}>
          <Text singleLine variant="display-medium" maxWidth={maxWidth}>
            {dateString}
          </Text>
        </CellWrapper>
      )
    }

    case 'boolean': {
      return (
        <CellWrapper justify={justify}>
          <Badge textVariant="mono" color="neutral-subtle">
            {cellData ? 'true' : 'false'}
          </Badge>
        </CellWrapper>
      )
    }

    case 'enum': {
      return (
        <CellWrapper justify={justify}>
          <Badge textVariant="mono" color="neutral-subtle">
            {parseValueFromSchema(cellData)}
          </Badge>
        </CellWrapper>
      )
    }

    case 'object': {
      return (
        <CellWrapper justify={justify}>
          <Badge textVariant="mono" color="neutral-subtle">
            {parseValueFromSchema(cellData)}
          </Badge>
        </CellWrapper>
      )
    }

    case 'reference': {
      return (
        <CellWrapper justify={justify}>
          <Badge textVariant="mono" color="neutral-subtle">
            {parseValueFromSchema(cellData)}
          </Badge>
        </CellWrapper>
      )
    }

    case 'references': {
      if (!Array.isArray(cellData)) {
        return
      }

      if (cellData.length > 3) {
        const length = cellData.length
        cellData = cellData.slice(0, 3)
        cellData.push('+' + (length - 3) + ' more')
      }

      return (
        <CellWrapper justify={justify}>
          <Stack gap={4} justify="start">
            {cellData.map((item, index) => (
              <Badge
                key={item.id + '-' + index}
                textVariant="mono"
                color="neutral-subtle"
              >
                {parseValueFromSchema(item)}
              </Badge>
            ))}
          </Stack>
        </CellWrapper>
      )
    }

    case 'json': {
      return (
        <CellWrapper justify={justify}>
          <Badge textVariant="mono" color="neutral-subtle">
            {JSON.stringify(cellData)}
          </Badge>
        </CellWrapper>
      )
    }

    // case 'binary': {
    //   return JSON.stringify(value)
    // }

    // case 'cardinality': {
    //   return JSON.stringify(value)
    // }

    default: {
      return (
        <CellWrapper justify={justify}>
          <Text singleLine variant="display-medium" maxWidth={maxWidth}>
            {JSON.stringify(cellData)}
          </Text>
        </CellWrapper>
      )
    }
  }
}
